import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  SearchParams,
  NotificationChannelRecord,
  TelegramLinkTokenResponse,
  OriginRecord,
  PlaceRecord,
  FacilityRecord,
  SiteRecord,
  AvailabilityRecord,
  UserPlan,
} from '../types';
import { api } from '../api/client';

export function useSearchFacilities(params: SearchParams) {
  return useQuery({
    queryKey: ['facilities', 'search', params],
    queryFn: () => api.facilities.search(params),
    enabled: Object.keys(params).length > 0,
  });
}

export function useFacility(id: string) {
  return useQuery({
    queryKey: ['facility', id],
    queryFn: () => api.facilities.getById(id),
    enabled: !!id,
  });
}

export function useAvailability(campsiteId: string, startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['availability', campsiteId, startDate, endDate],
    queryFn: () => api.availability.getForCampsite(campsiteId, startDate, endDate),
    enabled: !!campsiteId && !!startDate && !!endDate,
  });
}

export function useFacilityAvailability(
  facilityDbId: string,
  startDate: string,
  endDate: string
) {
  return useQuery({
    queryKey: ['facility-availability', facilityDbId, startDate, endDate],
    queryFn: async () => {
      const response = await api.availability.getForFacility(facilityDbId, startDate, endDate);
      return response;
    },
    enabled: !!facilityDbId && !!startDate && !!endDate,
  });
}

export function useRefreshFacility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (facilityId: string) => api.availability.refreshFacility(facilityId),
    onSuccess: (_data, facilityId) => {
      // Invalidate all availability queries for this facility
      queryClient.invalidateQueries({
        queryKey: ['facility-availability', facilityId],
        refetchType: 'active',
      });
    },
  });
}

export function useFeaturedParks() {
  return useQuery({
    queryKey: ['featured-parks'],
    queryFn: () => api.parks.featured(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useParksDirectory() {
  return useQuery({
    queryKey: ['parks-directory'],
    queryFn: () => api.parks.directory(),
    staleTime: 5 * 60 * 1000,
  });
}

export interface ParkWeather {
  current: { temperature: number; weatherCode: number };
  daily: Array<{ date: string; weatherCode: number; high: number; low: number }>;
}

interface OpenMeteoResponse {
  current?: { temperature_2m?: number; weather_code?: number };
  daily?: {
    time?: string[];
    weather_code?: number[];
    temperature_2m_max?: number[];
    temperature_2m_min?: number[];
  };
}

export function useParkWeather(lat: number | undefined, lon: number | undefined) {
  return useQuery({
    queryKey: ['park-weather', lat, lon],
    queryFn: async (): Promise<ParkWeather> => {
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min&temperature_unit=fahrenheit&timezone=auto&forecast_days=5`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Weather request failed: ${response.status}`);
      }
      const data = (await response.json()) as OpenMeteoResponse;
      if (data.current?.temperature_2m == null || data.current?.weather_code == null) {
        throw new Error('Weather response missing current conditions');
      }
      const time = data.daily?.time ?? [];
      return {
        current: {
          temperature: data.current.temperature_2m,
          weatherCode: data.current.weather_code,
        },
        daily: time.map((date, i) => ({
          date,
          weatherCode: data.daily?.weather_code?.[i] ?? 0,
          high: data.daily?.temperature_2m_max?.[i] ?? 0,
          low: data.daily?.temperature_2m_min?.[i] ?? 0,
        })),
      };
    },
    enabled: lat != null && lon != null,
    staleTime: 15 * 60 * 1000,
    retry: 1,
  });
}

export function useRequests() {
  return useQuery({
    queryKey: ['requests'],
    queryFn: () => api.requests.list(),
  });
}

export function useCreateRequest() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: {
      facilityId: string;
      startDate: string;
      endDate: string;
      stayLength: number;
      arrivalDays: Array<number | string>;
      siteType?: string;
      electric?: string;
      water?: boolean;
      sewer?: boolean;
      includeAdaSites?: boolean;
      isActive?: boolean;
    }) =>
      api.requests.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
}

export function useCreateRequestBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      facilityIds: string[];
      startDate: string;
      endDate: string;
      stayLength: number;
      arrivalDays: Array<number | string>;
      siteType?: string;
      electric?: string;
      water?: boolean;
      sewer?: boolean;
      includeAdaSites?: boolean;
      isActive?: boolean;
    }) => api.requests.createBatch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
}

export function useCancelRequest() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => api.requests.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
}

export function useUpdateRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: {
      id: number;
      data: {
        startDate?: string;
        endDate?: string;
        stayLength?: number;
        arrivalDays?: Array<number | string>;
        isActive?: boolean;
        siteType?: string;
        electric?: string;
        water?: boolean;
        sewer?: boolean;
        includeAdaSites?: boolean;
      };
    }) => api.requests.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.notifications.list(),
  });
}

export function useOrigins() {
  return useQuery({
    queryKey: ['discovery', 'origins'],
    queryFn: () => api.discovery.getOrigins(),
  });
}

export function usePlaces() {
  return useQuery({
    queryKey: ['discovery', 'places'],
    queryFn: () => api.discovery.getPlaces(),
  });
}

export function useCatalogFacilities() {
  return useQuery({
    queryKey: ['discovery', 'facilities'],
    queryFn: () => api.discovery.getFacilities(),
  });
}

export function useFacilitiesForPlace(placeId: string | null) {
  return useQuery({
    queryKey: ['discovery', 'facilities', placeId],
    queryFn: () => api.discovery.getFacilities({ placeId: placeId! }),
    enabled: !!placeId,
  });
}

export function useSites() {
  return useQuery({
    queryKey: ['discovery', 'sites'],
    queryFn: () => api.discovery.getSites(),
  });
}

export interface DiscoveryDataset {
  origins: OriginRecord[];
  places: PlaceRecord[];
  facilities: FacilityRecord[];
  sites: SiteRecord[];
}

export function useDiscoveryDataset() {
  return useQuery({
    queryKey: ['discovery', 'dataset'],
    queryFn: async (): Promise<DiscoveryDataset> => {
      const [origins, places, facilities, sites] = await Promise.all([
        api.discovery.getOrigins(),
        api.discovery.getPlaces(),
        api.discovery.getFacilities(),
        api.discovery.getSites(),
      ]);

      return {
        origins,
        places,
        facilities,
        sites,
      };
    },
  });
}

export function usePlaceDetails(id: string) {
  return useQuery({
    queryKey: ['discovery', 'place-details', id],
    queryFn: () => api.discovery.getPlaceDetails(id),
    enabled: !!id,
  });
}

export function usePlacesSummary() {
  return useQuery({
    queryKey: ['discovery', 'places-summary'],
    queryFn: () => api.discovery.getPlacesSummary(),
  });
}

export function useAvailablePlaces(
  params: { startDate: string; endDate: string; minNights?: number },
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['discovery', 'available-places', params.startDate, params.endDate, params.minNights ?? 1],
    queryFn: () => api.discovery.getAvailablePlaces(params.startDate, params.endDate, params.minNights),
    enabled,
  });
}

export function useAvailableFacilitiesForPlace(
  placeId: string,
  params: { startDate: string; endDate: string; minNights?: number },
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['discovery', 'available-facilities-for-place', placeId, params.startDate, params.endDate, params.minNights ?? 1],
    queryFn: () => api.discovery.getAvailableFacilitiesForPlace(placeId, params.startDate, params.endDate, params.minNights),
    enabled,
  });
}

export function useDiscoveryAvailabilityRange(
  params: {
    startDate: string;
    endDate: string;
    limit?: number;
  },
  enabled: boolean
) {
  return useQuery({
    queryKey: ['discovery', 'availability-range', params],
    queryFn: async (): Promise<AvailabilityRecord[]> =>
      api.discovery.getAvailabilityRange(params),
    enabled,
  });
}

export function useNotificationChannels(options?: {
  enabled?: boolean;
  refetchInterval?: number | false;
}) {
  return useQuery({
    queryKey: ['notification-channels'],
    queryFn: (): Promise<NotificationChannelRecord[]> => api.notificationChannels.list(),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval ?? false,
  });
}

export function useCreateTelegramLinkToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (): Promise<TelegramLinkTokenResponse> =>
      api.notificationChannels.createTelegramLinkToken(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
    },
  });
}

export function useDisconnectTelegram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.notificationChannels.disconnectTelegram(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
    },
  });
}

export function useConnectEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.notificationChannels.connectEmail(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
    },
  });
}

export function useDisconnectEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.notificationChannels.disconnectEmail(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
    },
  });
}

export function useUserPlan(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['billing', 'plan'],
    queryFn: (): Promise<UserPlan> => api.billing.getPlan(),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateCheckout() {
  return useMutation({
    mutationFn: ({ interval, email }: { interval: 'monthly' | 'annual'; email?: string }) =>
      api.billing.createCheckout(interval, email),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
  });
}

export function useCreatePortalSession() {
  return useMutation({
    mutationFn: () => api.billing.createPortalSession(),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
  });
}
